import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import {
  Accordion,
  DropdownInput,
  FilterBuilder,
  FilterBuilderFieldsType,
  Form,
  useForm,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  AttributeDataTypeType,
  FormChoiceAction,
} from "@validereinc/domain";
import React, { useEffect, useMemo, useState } from "react";

const singleFieldKey = "defaultQuestionKey";

const WorkflowConfigurationSingleFormChoice = ({
  choice,
  formSchemaId,
  onChange,
  index,
}: {
  choice: FormChoiceAction["choices"][0];
  formSchemaId: string;
  onChange: (formValues: FormChoiceAction["choices"][0]) => void;
  index: number;
}) => {
  const wrappedFilterValue = useMemo(() => {
    return choice.answer_filter?.["answer.value"] !== undefined
      ? {
          [singleFieldKey]: choice.answer_filter?.["answer.value"],
        }
      : {};
  }, [choice.answer_filter]);

  const [filter, setFilter] = useState(wrappedFilterValue);

  const isFormSchemaQueryEnabled = !!formSchemaId;

  const formSchemaQuery = useGetOneFormSchema(
    {
      id: formSchemaId,
    },
    { enabled: isFormSchemaQueryEnabled }
  );

  const formSchema = formSchemaQuery.data?.data;

  const sectionDropdownOptions = useMemo(
    () =>
      formSchema?.config.sections.map((section) => ({
        label: section.name,
        value: section.id,
      })),
    [JSON.stringify(formSchema?.config.sections)]
  );

  const form = useForm({
    defaultValues: {
      question_id: choice.answer_filter?.question_id ?? "",
      section_id: choice.answer_filter?.section_id ?? "",
      answer_value: choice.answer_filter?.["answer.value"] ?? "",
    },
  });

  const { question_id: selectedQuestionId, section_id: selectedSectionId } =
    choice.answer_filter ?? {};

  const selectedQuestion = useMemo(() => {
    if (!selectedQuestionId) return;
    return formSchema?.config.questions[selectedQuestionId];
  }, [selectedQuestionId, formSchema?.config.questions]);

  const questionDropdownOptions = useMemo(() => {
    const selectedSection = formSchema?.config.sections.find(
      (section) => section.id === selectedSectionId
    );
    return Object.entries(formSchema?.config.questions ?? {})
      .filter(
        ([questionId]) =>
          !selectedSectionId ||
          (selectedSection && selectedSection.questions.includes(questionId))
      )
      .map(([questionId, questionDefinition]) => ({
        value: questionId,
        label: questionDefinition.prompt,
      }));
  }, [JSON.stringify(formSchema?.config.questions), selectedSectionId]);

  const safeFieldsDefinition: FilterBuilderFieldsType = useMemo(() => {
    if (!selectedQuestion || !("data_type" in selectedQuestion))
      return {} as FilterBuilderFieldsType;
    if (
      (
        [
          AttributeDataType.STRING,
          AttributeDataType.NUMBER,
          AttributeDataType.BOOLEAN,
          AttributeDataType.DATE,
          AttributeDataType.PICK_LIST,
        ] as AttributeDataTypeType[]
      ).includes(selectedQuestion.data_type)
    ) {
      return {
        [singleFieldKey]: {
          data_type: selectedQuestion.data_type,
          display_name: "",
          pick_list_options: selectedQuestion?.pick_list_values ?? undefined,
        },
      } as FilterBuilderFieldsType;
    }
    return {} as FilterBuilderFieldsType;
  }, [selectedQuestion]);

  useEffect(() => {
    if (filter[singleFieldKey] !== undefined) {
      handleValueChange("answer_value", filter[singleFieldKey]);
    }
  }, [filter]);

  const handleValueChange = (
    key: "question_id" | "section_id" | "answer_value",
    value: string
  ) => {
    const transformedKey = key === "answer_value" ? "answer.value" : key;

    onChange({
      ...choice,
      answer_filter: {
        ...(choice.answer_filter ?? {}),
        [transformedKey]: value,
      },
    });
  };

  return (
    <Accordion defaultActiveKeys={["form-choice-filter"]}>
      <Accordion.AccordionPanel
        dataKey="form-choice-filter"
        title={`Question ${index + 1}`}
        variant="bordered"
      >
        <Form
          {...form}
          style={{ maxWidth: 432 }}
        >
          <DropdownInput
            name="section_id"
            options={sectionDropdownOptions}
            labelKey="label"
            valueKey="value"
            isRequired={false}
            isOptionalTextShown
            placeholder="Select a section..."
            label="Section"
            onChange={(sectionId: string) => {
              handleValueChange("section_id", sectionId);
            }}
          />
          <DropdownInput
            name="question_id"
            options={questionDropdownOptions}
            labelKey="label"
            valueKey="value"
            label="Question"
            isRequired
            onChange={(questionId: string) => {
              handleValueChange("question_id", questionId);
            }}
          />
          {Object.keys(safeFieldsDefinition).length > 0 && (
            <FilterBuilder
              fields={safeFieldsDefinition}
              filter={filter}
              setFilter={setFilter}
              variant="vertical"
              defaultSingleField={singleFieldKey}
            />
          )}
        </Form>
      </Accordion.AccordionPanel>
    </Accordion>
  );
};

export default WorkflowConfigurationSingleFormChoice;
